/**
 * Permission Resolver
 *
 * Логика вычисления прав пользователя.
 * Most permissive wins: при множественных ролях права объединяются.
 */

import { SECTIONS } from './sections';
import { Role, DataScope, ResolvedPermissions, ROLES } from './types';

/**
 * Вычисляет итоговые права пользователя для раздела.
 * Most permissive wins: объединяем скоупы всех ролей пользователя.
 *
 * @param userRoles - массив ролей пользователя из Keycloak
 * @param section - ID раздела
 * @returns вычисленные права
 */
export function resolvePermissions(
  userRoles: string[],
  section: string
): ResolvedPermissions {
  const perm = SECTIONS[section];

  // Раздел не найден - нет доступа
  if (!perm) {
    return {
      canAccess: false,
      readScopes: [],
      writeScopes: [],
      deleteScopes: [],
      hasFullRead: false,
      hasFullWrite: false,
      hasFullDelete: false,
      hiddenFields: [],
      readOnlyFields: [],
      capabilities: [],
    };
  }

  // Фильтруем только валидные роли пользователя для этого раздела
  const validRoles = userRoles.filter((r): r is Role =>
    perm.allowedRoles.includes(r as Role)
  );
  const authenticatedEmployeeScope = perm.authenticatedEmployeeScope;
  const hasAuthenticatedEmployeeAccess = Boolean(
    authenticatedEmployeeScope &&
      ((authenticatedEmployeeScope.readScope?.length ?? 0) > 0 ||
        (authenticatedEmployeeScope.writeScope?.length ?? 0) > 0 ||
        (authenticatedEmployeeScope.deleteScope?.length ?? 0) > 0),
  );

  // Нет подходящих ролей - нет доступа
  if (validRoles.length === 0 && !hasAuthenticatedEmployeeAccess) {
    return {
      canAccess: false,
      readScopes: [],
      writeScopes: [],
      deleteScopes: [],
      hasFullRead: false,
      hasFullWrite: false,
      hasFullDelete: false,
      hiddenFields: [],
      readOnlyFields: [],
      capabilities: [],
    };
  }

  // Собираем все скоупы из всех ролей (union - most permissive wins)
  const readScopes = new Set<DataScope>();
  const writeScopes = new Set<DataScope>();
  const deleteScopes = new Set<DataScope>();
  const deleteScopeConfig = perm.deleteScope ?? perm.writeScope;

  (authenticatedEmployeeScope?.readScope ?? []).forEach((s) => readScopes.add(s));
  (authenticatedEmployeeScope?.writeScope ?? []).forEach((s) => writeScopes.add(s));
  (authenticatedEmployeeScope?.deleteScope ?? []).forEach((s) => deleteScopes.add(s));

  for (const role of validRoles) {
    const rs = perm.readScope[role] || [];
    const ws = perm.writeScope[role] || [];
    const ds = deleteScopeConfig[role] || [];

    rs.forEach((s) => readScopes.add(s));
    ws.forEach((s) => writeScopes.add(s));
    ds.forEach((s) => deleteScopes.add(s));
  }

  const compactScopes = (scopes: Set<DataScope>): DataScope[] =>
    scopes.has('all') ? ['all'] : Array.from(scopes);

  const readScopesArray = compactScopes(readScopes);
  const writeScopesArray = compactScopes(writeScopes);
  const deleteScopesArray = compactScopes(deleteScopes);

  // Вычисляем field-level permissions
  // Поле ограничено если ЛЮБАЯ из ролей пользователя есть в списке ограничений
  const hiddenFields: string[] = [];
  const readOnlyFields: string[] = [];

  if (perm.fieldPermissions) {
    const { hidden, readOnly } = perm.fieldPermissions;

    // Hidden fields
    if (hidden) {
      for (const [field, restrictedRoles] of Object.entries(hidden)) {
        if (restrictedRoles && validRoles.some((r) => restrictedRoles.includes(r))) {
          hiddenFields.push(field);
        }
      }
    }

    // ReadOnly fields
    if (readOnly) {
      for (const [field, restrictedRoles] of Object.entries(readOnly)) {
        if (restrictedRoles && validRoles.some((r) => restrictedRoles.includes(r))) {
          readOnlyFields.push(field);
        }
      }
    }
  }

  // Вычисляем capabilities
  // Пользователь имеет capability если ЛЮБАЯ из его ролей есть в списке
  const capabilities: string[] = [];
  if (perm.capabilities) {
    for (const [cap, allowedRoles] of Object.entries(perm.capabilities)) {
      if (allowedRoles && validRoles.some((r) => allowedRoles.includes(r))) {
        capabilities.push(cap);
      }
    }
  }

  return {
    canAccess: true,
    readScopes: readScopesArray,
    writeScopes: writeScopesArray,
    deleteScopes: deleteScopesArray,
    hasFullRead: readScopesArray.includes('all'),
    hasFullWrite: writeScopesArray.includes('all'),
    hasFullDelete: deleteScopesArray.includes('all'),
    hiddenFields,
    readOnlyFields,
    capabilities,
  };
}

/**
 * Быстрая проверка доступа к разделу (без деталей скоупов)
 *
 * @param userRoles - массив ролей пользователя
 * @param section - ID раздела
 */
export function canAccessSection(userRoles: string[], section: string): boolean {
  const perm = SECTIONS[section];
  if (!perm) return false;
  const authenticatedEmployeeScope = perm.authenticatedEmployeeScope;
  if (
    authenticatedEmployeeScope &&
    ((authenticatedEmployeeScope.readScope?.length ?? 0) > 0 ||
      (authenticatedEmployeeScope.writeScope?.length ?? 0) > 0 ||
      (authenticatedEmployeeScope.deleteScope?.length ?? 0) > 0)
  ) {
    return true;
  }
  return userRoles.some((r) => perm.allowedRoles.includes(r as Role));
}

/**
 * Проверка права на запись в разделе
 *
 * @param userRoles - массив ролей пользователя
 * @param section - ID раздела
 */
export function canWriteInSection(userRoles: string[], section: string): boolean {
  const resolved = resolvePermissions(userRoles, section);
  return resolved.writeScopes.length > 0;
}

/**
 * Проверка права на удаление в разделе
 *
 * @param userRoles - массив ролей пользователя
 * @param section - ID раздела
 */
export function canDeleteInSection(userRoles: string[], section: string): boolean {
  const resolved = resolvePermissions(userRoles, section);
  return resolved.deleteScopes.length > 0;
}

/**
 * Получить права для всех разделов
 * Используется визуализатором для отображения полной карты прав
 *
 * @param userRoles - массив ролей пользователя
 */
export function resolveAllSections(
  userRoles: string[]
): Record<string, ResolvedPermissions> {
  const result: Record<string, ResolvedPermissions> = {};

  for (const section of Object.keys(SECTIONS)) {
    result[section] = resolvePermissions(userRoles, section);
  }

  return result;
}

/**
 * Получить полный конфиг для визуализатора
 */
export function getFullConfig() {
  return {
    sections: SECTIONS,
    roles: Object.values(ROLES),
  };
}

/**
 * Проверить, является ли строка валидной ролью
 */
export function isValidRole(role: string): role is Role {
  return Object.values(ROLES).includes(role as Role);
}

/**
 * Получить все доступные роли
 */
export function getAllRoles(): Role[] {
  return Object.values(ROLES);
}
